import { CAMPAIGN_START_UTC, CAMPAIGN_END_UTC } from '@aion2/shared';

/**
 * The public schedule calendar (Coming Soon page).
 *
 * Dates and times are the editorial calendar; titles and descriptions are
 * messages (`pages.home.cs.schedule.events.<key>`) so they translate. Times
 * are wall-clock in SCHEDULE_TZ, the broadcast desk's zone, and render as
 * "9 PM ET" style labels in every language.
 *
 * Only confirmed campaign beats are listed. Match-ups and broadcast slots are
 * added here as they are fixed; an empty day renders as a quiet cell rather
 * than an invented event.
 */

/**
 * Calendar display range for the Coming Soon schedule grid.
 *
 * These control which days are rendered in calendarWeeks() and are independent
 * of CAMPAIGN_START_UTC / CAMPAIGN_END_UTC, which govern scoring and site-mode
 * logic. Adjust these to change what the visitor-facing calendar shows without
 * touching any back-end constants.
 */
export const SCHEDULE_DISPLAY_START = '2026-09-23';
export const SCHEDULE_DISPLAY_END = '2026-10-15';
export const SCHEDULE_TZ = 'America/Los_Angeles';
export const SCHEDULE_TZ_LABEL = 'PDT';

export type ScheduleEventKind = 'broadcast' | 'match' | 'milestone';

export type ScheduleEventStatus = 'live' | 'upcoming' | 'final' | 'finale';

export interface ScheduleEvent {
  /** Calendar day in SCHEDULE_TZ, YYYY-MM-DD. */
  date: string;
  /** Wall-clock start in SCHEDULE_TZ, HH:MM (24h). Omit for all-day beats. */
  time?: string;
  /** Message key under pages.home.cs.schedule.events. */
  key: string;
  kind: ScheduleEventKind;
  /** Optional override for the mobile status label. */
  status?: ScheduleEventStatus;
  /** Renders the "tournament finale" treatment on the day cell. */
  finale?: boolean;
  /** Renders the event as a wildcard release card. */
  isWildcard?: boolean;
  /**
   * When true, the main title/description card is hidden — only the wildcard
   * card is shown. Useful for days where the main event is not yet announced
   * but the wildcard slot is confirmed.
   */
  hideMainCard?: boolean;
}

export const SCHEDULE_EVENTS: readonly ScheduleEvent[] = [
  { date: '2026-09-23', time: '10:00', key: '9-23', kind: 'broadcast', status: 'upcoming' },
  { date: '2026-09-30', time: '10:00', key: '09-30', kind: 'broadcast', status: 'live',  isWildcard: true },
  { date: '2026-10-03', key: '10-03', kind: 'milestone', status: 'upcoming', isWildcard: true, hideMainCard: true },
  { date: '2026-10-05', time: '10:00', key: '10-05', kind: 'broadcast', status: 'final',isWildcard: true  },
  { date: '2026-10-09', time: '21:00', key: '10-09', kind: 'milestone', status: 'final', isWildcard: true, hideMainCard: true },
  { date: '2026-10-12', key: '10-12', kind: 'milestone', status: 'final', isWildcard: true, hideMainCard: true },
  { date: '2026-10-15', time: '10:00', key: '10-15', kind: 'broadcast', status: 'finale' },
];

/** Where the official broadcast lives. */
export const TWITCH_CHANNEL_URL = 'https://www.twitch.tv/aion2official';

/**
 * The calendar spans whole Sunday-to-Saturday weeks covering the campaign, so
 * the grid always lines up with weekday headers whatever day the campaign
 * starts on.
 */
export function calendarWeeks(): string[][] {
  const start = SCHEDULE_DISPLAY_START;
  const end = SCHEDULE_DISPLAY_END;
  const first = sundayOf(start);
  const weeks: string[][] = [];
  let cursor = first;
  while (cursor <= end || weeks.length === 0) {
    const week: string[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(cursor);
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
    if (cursor > end) break;
  }
  return weeks;
}

/** Calendar date of an instant in the schedule zone, as YYYY-MM-DD. */
function dayInZone(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: SCHEDULE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

export function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = Date.UTC(y!, m! - 1, d! + n);
  return new Date(t).toISOString().slice(0, 10);
}

function sundayOf(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay(); // 0 = Sunday
  return addDays(ymd, -dow);
}

/** The instant an event starts, for "completed / upcoming" and for formatting. */
export function eventInstant(e: ScheduleEvent): Date {
  const [y, m, d] = e.date.split('-').map(Number);
  const [hh, mm] = (e.time ?? '00:00').split(':').map(Number);
  // Resolve the zone's offset for that calendar moment via a round trip.
  const guess = Date.UTC(y!, m! - 1, d!, hh!, mm!);
  const asZone = new Date(
    new Intl.DateTimeFormat('en-US', {
      timeZone: SCHEDULE_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(guess)) + ' UTC',
  ).getTime();
  const offset = asZone - guess;
  return new Date(guess - offset);
}

export function isCampaignDay(ymd: string): boolean {
  // return ymd >= dayInZone(new Date(CAMPAIGN_START_UTC)) && ymd <= dayInZone(new Date(CAMPAIGN_END_UTC));
  return ymd >= "2026-09-30" && ymd <= "2026-10-15";

}

/**
 * The "War progress" timeline on the Converge (/schedule) page: four fixed
 * beats, each spanning a calendar range in SCHEDULE_TZ.
 */
export interface TimelineItem {
  id: string;
  /** Message key under pages.schedule.converge.timeline. */
  titleKey: string;
  /** Translation params for the title (e.g. the period number), if any. */
  titleParams?: Record<string, string>;
  /** First calendar day in SCHEDULE_TZ, YYYY-MM-DD. */
  start: string;
  /** Last calendar day in SCHEDULE_TZ, YYYY-MM-DD (inclusive). Same as start for single-day beats. */
  end: string;
}

export const TIMELINE_ITEMS: readonly TimelineItem[] = [
  { id: 'opening-draft', titleKey: 'timeline.openingDraft', start: '2026-09-23', end: '2026-09-23' },
  { id: 'period-01', titleKey: 'timeline.period', titleParams: { number: '01' }, start: '2026-09-30', end: '2026-10-07' },
  { id: 'period-02', titleKey: 'timeline.period', titleParams: { number: '02' }, start: '2026-10-08', end: '2026-10-14' },
  { id: 'final-battle', titleKey: 'timeline.finalBattle', start: '2026-10-15', end: '2026-10-15' },
];

/**
 * The instant 10:00 America/Los_Angeles on a given calendar day — the
 * product owner's official day boundary for the timeline's "current" state.
 * PDT (UTC-7) holds through 1 Nov 2026; this fixed offset needs revisiting
 * if the timeline is still live once the US falls back to PST.
 */
export function timelineDayBoundary(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!, 17, 0));
}

/**
 * The id of the timeline item that is "current" at instant `now`, or null
 * when `now` falls in a gap (before the first item starts, or after the
 * last one ends). An item is current from 10:00 PT on its start date until
 * 10:00 PT on the day after its end date.
 */
export function currentTimelineItemId(
  now: Date,
  items: readonly TimelineItem[] = TIMELINE_ITEMS,
): string | null {
  const t = now.getTime();
  for (const item of items) {
    const from = timelineDayBoundary(item.start).getTime();
    const until = timelineDayBoundary(addDays(item.end, 1)).getTime();
    if (t >= from && t < until) return item.id;
  }
  return null;
}
